import { useCallback, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { SpinnerIcon } from '../onboarding/icons';
import type {
  ListResult,
  ListQueryParams,
  SyncStateFilter,
} from './types';

/**
 * Shared sync-state filter tabs. The four states map to the unified sync_state
 * vocabulary (data-model.md §1.1). `all` is the default (no WHERE clause).
 *
 * `pending` is only shown for entities that can reach it (products); callers
 * that don't need it pass a filtered list.
 */
const STATE_TABS: { value: SyncStateFilter; label: string; tone: BadgeTone; }[] = [
  { value: 'all', label: 'All', tone: 'neutral' },
  { value: 'synced', label: 'Synced', tone: 'success' },
  { value: 'pending', label: 'Pending', tone: 'brand' },
  { value: 'broken', label: 'Broken', tone: 'warn' },
  { value: 'ignored', label: 'Ignored', tone: 'neutral' },
];

type BadgeTone = 'neutral' | 'success' | 'warn' | 'brand';

/**
 * Hook: manage the filter/search/pagination state for a sync-status list view
 * and fetch the matching page from the backend.
 *
 * @param basePath  The API path, e.g. 'admin/products'.
 * @param pageSize  Rows per page (default 25 — small enough to scan, large
 *                  enough to cover most tenants' catalogs on one page).
 */
export function useSyncList<T>(
  basePath: string,
  pageSize = 25,
  extraParams: Record<string, string | number | boolean | undefined> = {}
) {
  const [state, setState] = useState<SyncStateFilter>('all');
  // Debounced search is gone (QA: the generic search box was removed from
  // every view — the customer typeahead + dedicated name/email filters
  // replace it). The param is retained for the lookup endpoints only.
  const [offset, setOffset] = useState(0);
  // M19 T3: universal Customer filter + Churn-watch (cancel-at-period-end).
  const [customer, setCustomer] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState<boolean>(false);
  // Column sort (server-side, allowlisted in the handler).
  const [sort, setSort] = useState<string | null>(null);
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');

  const params: ListQueryParams = useMemo(
    () => ({
      state,
      limit: pageSize,
      offset,
      customer: customer ?? undefined,
      cancelAtPeriodEnd: cancelAtPeriodEnd || undefined,
      sort: sort ?? undefined,
      dir: sort ? dir : undefined,
      ...extraParams,
    }),
    [state, pageSize, offset, customer, cancelAtPeriodEnd, sort, dir, extraParams]
  );

  const buildQuery = useCallback(
    (p: ListQueryParams) => {
      const qs = new URLSearchParams();
      if (p.state && p.state !== 'all') qs.set('state', p.state);
      if (p.limit) qs.set('limit', String(p.limit));
      if (p.offset) qs.set('offset', String(p.offset));
      if (p.customer) qs.set('customer', p.customer);
      if (p.cancelAtPeriodEnd) qs.set('cancelAtPeriodEnd', '1');
      if (p.sort) qs.set('sort', p.sort);
      if (p.sort && p.dir) qs.set('dir', p.dir);
      // View-specific extra params (e.g. customers name/email, subs status).
      if (p.name) qs.set('name', p.name);
      if (p.email) qs.set('email', p.email);
      if (p.status) qs.set('status', p.status);
      const suffix = qs.toString();
      return suffix ? `${basePath}?${suffix}` : basePath;
    },
    [basePath]
  );

  const fetcher = useCallback(
    (signal: AbortSignal) =>
      api.get<ListResult<T>>(buildQuery(params), { signal }),
    [buildQuery, params]
  );
  const { data, loading, error, refetch } = useAsync(fetcher, [
    params.state,
    params.limit,
    params.offset,
    params.customer,
    params.cancelAtPeriodEnd,
    params.sort,
    params.dir,
    params.name,
    params.email,
    params.status,
  ]);

  /** Toggle a sortable column: same column → flip dir; new column → desc. */
  const toggleSort = useCallback(
    (key: string) => {
      setDir((prevDir) => (sort === key ? (prevDir === 'desc' ? 'asc' : 'desc') : 'desc'));
      setSort(key);
      setOffset(0);
    },
    [sort]
  );

  return {
    data,
    loading,
    error,
    refetch,
    // Controls the hook exposes to the view.
    state,
    setState,
    offset,
    setOffset,
    pageSize,
    // M19 T3: customer + churn-watch filters.
    customer,
    setCustomer,
    cancelAtPeriodEnd,
    setCancelAtPeriodEnd,
    // Column sort.
    sort,
    dir,
    toggleSort,
  };
}

/**
 * Filter-tab row. Shared chrome across the sync-state list views. The generic
 * free-text search box was removed (QA: every view now uses the customer
 * typeahead + dedicated name/email filters instead).
 */
export function SyncStateTabs({
  state,
  setState,
  hidePending = false,
}: {
  state: SyncStateFilter;
  setState: (s: SyncStateFilter) => void;
  /** Hide the 'Pending' tab (customers/subscriptions never reach pending). */
  hidePending?: boolean;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <div className="flex flex-wrap gap-1">
        {STATE_TABS.filter((t) => !hidePending || t.value !== 'pending').map(
          (tab) => (
            <button
              key={tab.value}
              onClick={() => setState(tab.value)}
              className={[
                'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                state === tab.value
                  ? 'bg-brand-600 text-white'
                  : 'bg-ink-100 text-ink-600 hover:bg-ink-200',
              ].join(' ')}
            >
              {tab.label}
            </button>
          )
        )}
      </div>
    </div>
  );
}

/**
 * Pager — prev/next + page indicator. Uses `total` + `pageSize` to compute
 * page count; disables prev/next at the ends.
 */
export function Pager({
  total,
  offset,
  pageSize,
  setOffset,
}: {
  total: number;
  offset: number;
  pageSize: number;
  setOffset: (o: number) => void;
}) {
  if (total === 0) return null;
  const page = Math.floor(offset / pageSize) + 1;
  const totalPages = Math.ceil(total / pageSize);
  return (
    <div className="mt-4 flex items-center justify-between text-sm text-ink-500">
      <span>
        Showing {offset + 1}–{Math.min(offset + pageSize, total)} of {total}
      </span>
      <div className="flex items-center gap-2">
        <button
          className="btn-ghost px-2 py-1 disabled:opacity-30"
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - pageSize))}
        >
          ← Prev
        </button>
        <span className="text-xs">
          Page {page} of {totalPages}
        </span>
        <button
          className="btn-ghost px-2 py-1 disabled:opacity-30"
          disabled={offset + pageSize >= total}
          onClick={() => setOffset(offset + pageSize)}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

/** A small sync-state pill, color-coded by state. */
export function SyncStateBadge({ state }: { state: string; }) {
  const cls =
    state === 'synced'
      ? 'bg-success-500/10 text-success-600'
      : state === 'pending'
        ? 'bg-brand-50 text-brand-600'
        : state === 'broken'
          ? 'bg-warn-500/10 text-warn-600'
          : state === 'ignored'
            ? 'bg-ink-100 text-ink-500'
            : 'bg-ink-100 text-ink-600';
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {state}
    </span>
  );
}

/** Loading + empty + error states shared across the views. */
export function ListStates({
  loading,
  error,
  empty,
  onRetry,
}: {
  loading: boolean;
  error: Error | null;
  empty: boolean;
  onRetry: () => void;
}) {
  if (error) {
    return (
      <div className="rounded-lg border border-danger-500/30 bg-danger-500/5 p-4 text-sm text-ink-600">
        <p className="font-medium text-danger-600">Couldn't load the list.</p>
        <p className="mt-1">{error.message}</p>
        <button className="btn-secondary mt-2" onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-lg border border-ink-200 bg-white py-12 text-sm text-ink-500">
        <SpinnerIcon className="h-5 w-5 text-brand-500" />
        Loading…
      </div>
    );
  }
  if (empty) {
    return (
      <div className="rounded-lg border border-dashed border-ink-200 p-8 text-center text-sm text-ink-400">
        No matching records.
      </div>
    );
  }
  return null;
}

// ─── Sortable column header ────────────────────────────────────────────────

/**
 * A `<th>` that toggles server-side sort when clicked. Shows the column label
 * with a direction caret that reflects the active sort (▲ desc / ▼ asc).
 *
 * Sort is server-side (the DO handler allowlists columns), so this just emits
 * the logical sort key + lets the parent hook build the query. The first click
 * on a new column sorts descending; subsequent clicks flip asc↔desc.
 *
 * Non-sortable columns render a plain `<th>` (pass no `sortKey`).
 */
export function SortableTh({
  label,
  sortKey,
  activeSort,
  sortDir,
  onSort,
  align = 'left',
}: {
  label: string;
  /** Logical sort key handled by the backend. Omit for a non-sortable column. */
  sortKey?: string;
  activeSort: string | null;
  sortDir: 'asc' | 'desc';
  onSort: (key: string) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const alignCls =
    align === 'right'
      ? 'text-right'
      : align === 'center'
        ? 'text-center'
        : 'text-left';
  if (!sortKey) {
    return (
      <th className={`px-4 py-2 font-medium ${alignCls}`}>{label}</th>
    );
  }
  const active = activeSort === sortKey;
  const caret = active ? (sortDir === 'desc' ? '▼' : '▲') : '↕';
  return (
    <th className={`px-4 py-2 font-medium ${alignCls}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={[
          'inline-flex items-center gap-1 transition-colors hover:text-ink-800',
          active ? 'text-ink-800' : 'text-ink-500',
          align === 'right' ? 'flex-row-reverse' : '',
        ].join(' ')}
        aria-sort={
          active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
        }
      >
        <span>{label}</span>
        <span className="text-[10px] opacity-70">{caret}</span>
      </button>
    </th>
  );
}
